import { useLocation } from '@tanstack/react-router'
import { claimTypesEnum } from 'User/utils/claims'
import { useState } from 'react'

import IconRoyalty from '@mui/icons-material/AccountBalance'
import IconShield from '@mui/icons-material/AdminPanelSettings'
import IconBundle from '@mui/icons-material/AutoAwesomeMotion'
import IconEdition from '@mui/icons-material/Book'
import IconCloudDownload from '@mui/icons-material/CloudDownload'
import IconDashboard from '@mui/icons-material/Dashboard'
import IconError from '@mui/icons-material/ErrorOutlined'
import IconEvent from '@mui/icons-material/Event'
import IconSeries from '@mui/icons-material/FolderCopy'
import IconAssets from '@mui/icons-material/Image'
import IconWork from '@mui/icons-material/LibraryBooks'
import IconConcept from '@mui/icons-material/LightbulbOutlined'
import IconListAlt from '@mui/icons-material/ListAlt'
import IconLogout from '@mui/icons-material/Logout'
import IconMenu from '@mui/icons-material/Menu'
import IconSuperVisor from '@mui/icons-material/SupervisorAccount'
import Badge from '@mui/material/Badge'
import BottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { Authentication } from 'Common/auth/types'
import ColorModeToggle from 'Common/components/theme/ColorModeToggle'
import { useErrorStore } from 'Common/store/useErrorStore'
import { hasClaim, hasClaimWithValue } from 'Common/utils/user/auth'

import {
    RouterBottomNavigationAction,
    RouterLink,
    RouterListItemButton,
} from '../links/routerLinks'
import { ErrorListDialog } from './ErrorListDialog'
import { NewDrawerItem } from './NewDrawerListItem'

type Props = {
    auth: Authentication
    logout: () => void
}

const drawerWidth = 230
const miniDrawerWidth = 56
const drawerHeaderHeight = 72
const navRowHeight = 48
export const bottomNavHeight = 56

const bottomNavLinks = ['/concepts', '/works', '/work/editions', '/assets']

const activeBackground = 'rgba(255, 255, 255, 0.14)'
const hoverBackground = 'rgba(255, 255, 255, 0.08)'

const activeItemSx = {
    color: 'inherit',
    textDecoration: 'none',
}

const inSection = (pathname: string, base: string) =>
    pathname === base || pathname.startsWith(`${base}/`)

const workSubSections = ['works', 'editions', 'launchbrief', 'salessheet', 'activities']

const workDetailSegment = (pathname: string) => {
    const match = /^\/work\/([^/]+)(?:\/|$)/.exec(pathname)
    const segment = match?.[1]
    return segment && !workSubSections.includes(segment) ? segment : undefined
}

const isEditionDetail = (pathname: string) =>
    !!workDetailSegment(pathname) && /^\/work\/[^/]+\/edition(\/|$)/.test(pathname)

const isWorkDetail = (pathname: string) =>
    !!workDetailSegment(pathname) && !isEditionDetail(pathname)

// Ordered most-specific-first: the first match wins, so /work/:id/edition/:id resolves to
// Editions rather than Works, and /contacts/lists to Selection lists rather than Contacts.
const navSections: { link: string; matches: (pathname: string) => boolean }[] = [
    {
        link: '/work/editions',
        matches: (p) =>
            inSection(p, '/work/editions') ||
            inSection(p, '/work/salessheet') ||
            isEditionDetail(p),
    },
    { link: '/work/activities', matches: (p) => inSection(p, '/work/activities') },
    {
        link: '/works',
        matches: (p) =>
            inSection(p, '/works') || inSection(p, '/work/launchbrief') || isWorkDetail(p),
    },
    { link: '/contacts/lists', matches: (p) => p.startsWith('/contacts/list') },
    { link: '/contacts', matches: (p) => inSection(p, '/contacts') || p.startsWith('/contact/') },
    { link: '/concepts', matches: (p) => inSection(p, '/concepts') || p.startsWith('/concept/') },
    { link: '/royalty/agreements', matches: (p) => inSection(p, '/royalty') },
    { link: '/bundle/list', matches: (p) => inSection(p, '/bundle') },
    { link: '/series/list', matches: (p) => inSection(p, '/series') },
    { link: '/assets', matches: (p) => inSection(p, '/assets') },
    { link: '/mydownloads', matches: (p) => inSection(p, '/mydownloads') },
    { link: '/administration', matches: (p) => inSection(p, '/administration') },
    { link: '/mywork', matches: (p) => inSection(p, '/mywork') || p === '/' },
]

const activeNavLink = (pathname: string) =>
    navSections.find((section) => section.matches(pathname))?.link

const navLabelSx = {
    '@keyframes navLabelFadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
    animation: 'navLabelFadeIn 200ms ease-out',
}

type NavRowProps = {
    label: string
    icon: React.ReactNode
    mini: boolean
    to?: string
    forceActive?: boolean
    onClick?: () => void
}

function NavRow(props: Readonly<NavRowProps>) {
    const { label, icon, mini, to, forceActive = false, onClick } = props

    const buttonSx = {
        ...(to ? activeItemSx : { color: 'inherit' }),
        ...(forceActive ? { backgroundColor: activeBackground } : {}),
        justifyContent: mini ? 'center' : 'flex-start',
        minHeight: navRowHeight,
    }

    const content = (
        <>
            <ListItemIcon sx={mini ? { minWidth: 0, justifyContent: 'center' } : undefined}>
                {icon}
            </ListItemIcon>
            {!mini && (
                <ListItemText
                    primary={label}
                    sx={navLabelSx}
                    slotProps={{
                        primary: { noWrap: true },
                    }}
                />
            )}
        </>
    )

    return (
        <Tooltip title={mini ? label : ''} placement="right">
            {to ? (
                <RouterListItemButton to={to} onClick={onClick} sx={buttonSx} nativeButton={false}>
                    {content}
                </RouterListItemButton>
            ) : (
                <ListItemButton onClick={onClick} sx={buttonSx}>
                    {content}
                </ListItemButton>
            )}
        </Tooltip>
    )
}

export default function MainNavigationNew(props: Readonly<Props>) {
    const { auth, logout } = props
    const theme = useTheme()
    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'))
    const [showMobileNav, setShowMobileNav] = useState(false)
    const [showErrorList, setShowErrorList] = useState(false)
    const [isMini, setIsMini] = useState(false)
    const errors = useErrorStore((s) => s.errors)
    const { pathname } = useLocation()

    const drawerItems: (NewDrawerItem & {
        displayDividerBefore?: boolean
        displayDividerAfter?: boolean
    })[] = [
        { link: '/mywork', primary: 'My work', icon: IconDashboard },
        {
            link: '/royalty/agreements',
            primary: 'Royalty',
            icon: IconRoyalty,
            isActive: hasClaimWithValue(auth, 'opus_royalty', 'user'),
        },
        { link: '/concepts', primary: 'Concepts', icon: IconConcept },
        { link: '/works', primary: 'Works', icon: IconWork },
        { link: '/work/editions', primary: 'Editions', icon: IconEdition },
        { link: '/bundle/list', primary: 'Bundles', icon: IconBundle },
        {
            link: '/series/list',
            primary: 'Series',
            icon: IconSeries,
            isActive: hasClaim(auth, claimTypesEnum.enum.opus_work),
        },
        {
            link: '/work/activities',
            primary: 'Activities',
            icon: IconEvent,
        },
        {
            link: '/assets',
            primary: 'Assets',
            icon: IconAssets,
            isActive: hasClaim(auth, claimTypesEnum.enum.opus_asset),
        },
        { link: '/contacts', primary: 'Contacts', icon: IconSuperVisor },
        { link: '/contacts/lists', primary: 'Selection lists', icon: IconListAlt },
        { link: '/mydownloads', primary: 'Downloads', icon: IconCloudDownload },
    ].filter((item) => item !== null && (item.isActive === undefined || item.isActive))

    const showAdministration =
        hasClaimWithValue(auth, 'opus_user', 'admin') ||
        hasClaimWithValue(auth, 'opus_user', 'customeradmin')

    const bottomNavItems = bottomNavLinks
        .map((link) => drawerItems.find((item) => item.link === link))
        .filter((item) => item !== undefined)

    const moreItems = drawerItems.filter((item) => !bottomNavLinks.includes(item.link ?? ''))

    const activeLink = activeNavLink(pathname)

    const activeBottomNavLink =
        activeLink && bottomNavLinks.includes(activeLink) ? activeLink : false

    const renderNavItems = (items: typeof drawerItems, mini = false) =>
        items.map((item) => {
            const IconComponent = item.icon
            return (
                <div key={`nav-${item.primary}`}>
                    {item.displayDividerBefore && (
                        <Divider key={`divider-before-${item.primary}`} sx={{ mb: 1, pb: 1 }} />
                    )}
                    <NavRow
                        label={item.primary}
                        icon={<IconComponent />}
                        mini={mini}
                        to={item.link ?? ''}
                        forceActive={item.link === activeLink}
                        onClick={() => setShowMobileNav(false)}
                    />
                    {item.displayDividerAfter && (
                        <Divider key={`divider-after-${item.primary}`} sx={{ mb: 1, pt: 1 }} />
                    )}
                </div>
            )
        })

    const footer = (mini = false) => (
        <Box sx={{ flexShrink: 0 }}>
            {showAdministration && (
                <>
                    <NavRow
                        label="Administration"
                        icon={<IconShield />}
                        mini={mini}
                        to="/administration"
                        forceActive={activeLink === '/administration'}
                        onClick={() => setShowMobileNav(false)}
                    />
                    <ColorModeToggle mini={mini} />
                </>
            )}
            <Divider />
            {errors.length > 0 && (
                <NavRow
                    label="Errors"
                    icon={<IconError color="error" />}
                    mini={mini}
                    onClick={() => {
                        setShowMobileNav(false)
                        setShowErrorList(true)
                    }}
                />
            )}
            <NavRow label="Logout" icon={<IconLogout />} mini={mini} onClick={logout} />
        </Box>
    )

    const drawerHeader = (mini: boolean) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                flexShrink: 0,
                minHeight: drawerHeaderHeight,
                pl: 1,
                pr: 0,
            }}
        >
            <Tooltip title={mini ? 'Expand menu' : 'Collapse menu'} placement="right">
                <IconButton
                    color="inherit"
                    aria-label={mini ? 'Expand menu' : 'Collapse menu'}
                    onClick={() => setIsMini(!mini)}
                    sx={{ alignSelf: 'flex-start', mt: 1.5, flexShrink: 0 }}
                >
                    <IconMenu />
                </IconButton>
            </Tooltip>
            {!mini && (
                <RouterLink
                    to="/mywork"
                    underline="none"
                    onClick={() => setShowMobileNav(false)}
                    sx={{ display: 'flex', alignItems: 'center', minWidth: 0, ml: 1 }}
                >
                    <Box
                        component="img"
                        src="/assets/logo_core.png"
                        alt="Mockingbird"
                        sx={{ height: 48, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
                    />
                </RouterLink>
            )}
        </Box>
    )

    const navContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {drawerHeader(isMini)}
            <List
                key="main-nav"
                disablePadding
                sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}
            >
                <Divider sx={{ mb: 1 }} />
                {renderNavItems(drawerItems, isMini)}
            </List>
            {footer(isMini)}
        </Box>
    )

    const moreContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
            <Box
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    pt: 1.5,
                    pb: 0.5,
                }}
            >
                <Box
                    sx={{
                        width: 32,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.4)',
                    }}
                />
            </Box>
            <List disablePadding sx={{ flexGrow: 1, overflowY: 'auto' }}>
                {renderNavItems(moreItems)}
            </List>
            {footer()}
        </Box>
    )

    const navSurfaceSx = {
        backgroundColor: '#24413F',
        color: '#FFFFFF',
        '& .MuiListItemIcon-root': { color: 'inherit' },
        '& .MuiDivider-root': { borderColor: 'rgba(255, 255, 255, 0.24)' },
        '& .Mui-selected': { backgroundColor: activeBackground },
        '& .MuiListItemButton-root:hover': { backgroundColor: hoverBackground },
        '& .MuiListItemButton-root.active:hover': { backgroundColor: activeBackground },
        ...theme.applyStyles('dark', {
            backgroundColor: '#1a1a1a',
        }),
    }

    return (
        <>
            <Drawer
                variant="permanent"
                anchor="left"
                open
                sx={{
                    width: isMini ? miniDrawerWidth : drawerWidth,
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create('width', {
                        duration: theme.transitions.duration.shorter,
                    }),
                    '& .MuiDrawer-paper': {
                        width: isMini ? miniDrawerWidth : drawerWidth,
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                        transition: theme.transitions.create('width', {
                            duration: theme.transitions.duration.shorter,
                        }),
                        ...navSurfaceSx,
                    },
                    display: { xs: 'none', md: 'flex' },
                    displayPrint: 'none',
                }}
            >
                {navContent}
            </Drawer>
            {isBelowMd && (
                <>
                    <SwipeableDrawer
                        anchor="bottom"
                        open={showMobileNav}
                        onClose={() => setShowMobileNav(false)}
                        onOpen={() => setShowMobileNav(true)}
                        disableSwipeToOpen
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            displayPrint: 'none',
                            '& .MuiDrawer-paper': {
                                borderTopLeftRadius: 12,
                                borderTopRightRadius: 12,
                                pb: 'env(safe-area-inset-bottom)',
                                ...navSurfaceSx,
                            },
                        }}
                    >
                        {moreContent}
                    </SwipeableDrawer>
                    <BottomNavigation
                        value={activeBottomNavLink}
                        showLabels
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: (theme) => theme.zIndex.appBar,
                            displayPrint: 'none',
                            height: 'auto',
                            minHeight: bottomNavHeight,
                            pb: 'env(safe-area-inset-bottom)',
                            borderTop: '1px solid rgba(255, 255, 255, 0.24)',
                            backgroundColor: '#24413F',
                            '& .MuiBottomNavigationAction-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                minWidth: 0,
                            },
                            '& .MuiBottomNavigationAction-root.Mui-selected': {
                                color: '#FFFFFF',
                            },
                        }}
                    >
                        {bottomNavItems.map((item) => {
                            const IconComponent = item.icon
                            return (
                                <RouterBottomNavigationAction
                                    key={`bottom-nav-${item.primary}`}
                                    nativeButton={false}
                                    to={item.link ?? ''}
                                    value={item.link ?? ''}
                                    label={item.primary}
                                    icon={<IconComponent />}
                                />
                            )
                        })}
                        <BottomNavigationAction
                            key="bottom-nav-more"
                            aria-label="Open navigation"
                            onClick={() => setShowMobileNav(true)}
                            label="More"
                            icon={
                                <Badge
                                    color="error"
                                    variant="dot"
                                    invisible={errors.length === 0}
                                    overlap="circular"
                                >
                                    <IconMenu />
                                </Badge>
                            }
                        />
                    </BottomNavigation>
                </>
            )}
            {showErrorList && <ErrorListDialog onClose={() => setShowErrorList(false)} />}
        </>
    )
}
